import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GraduationCap, MapPin, Compass } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { ApplyDialog, CompanySheet } from "@/components/CompanyBits";

export default function Internships() {
  const { lang, user, loading: authLoading } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [apply, setApply] = useState(false);
  const [loading, setLoading] = useState(false);

  // Filter mode: "all" (global) OR "near_me" (constrained to user's saved region).
  const near = params.get("near") === "me";
  const canFilterNear = Boolean(user?.home_country);

  const activeCountry = near && user?.home_country ? user.home_country : null;
  const activeRegion = near && user?.home_region ? user.home_region : null;
  const activeCity = near && user?.home_city ? user.home_city : null;

  useEffect(() => {
    // Never fetch until auth has finished — otherwise the pre-auth call (with
    // `activeCountry=null`) races the post-auth call and overwrites the results.
    if (authLoading) return;
    // "Near me" requires a resolved user with a saved country. If the user is
    // still null here, they are anonymous — don't fetch, we'll show the CTA.
    if (near && !user?.home_country) { setItems([]); setLoading(false); return; }

    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    const q = { hosts_interns: true, limit: 300 };
    if (activeCountry) q.country = activeCountry;
    // We deliberately DON'T send the city server-side (too many spelling variants
    // in the DB) — we narrow it on the client with a cascade below.
    http.get("/companies", { params: q, signal: controller.signal })
      .then((r) => {
        if (cancelled) return;
        // HARD GUARD: never leak results from another country when the user
        // asked for "near me". Some pinned major-employers may sit in the top
        // of the priority sort — filter them out defensively.
        const all = (r.data || []).filter((c) => !activeCountry || c.country === activeCountry);
        let list = all;

        // Cascade: city exact → region substring → country. At each level we
        // require ≥ 3 hits, otherwise we fall back to the broader scope so the
        // user isn't left staring at 0 result when their exact city has none.
        if (activeCity) {
          const kw = activeCity.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
          const byCity = all.filter((c) => {
            const cn = (c.city || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            return cn === kw || cn.includes(kw);
          });
          if (byCity.length >= 3) list = byCity;
        }
        if (list === all && activeRegion) {
          const kw = activeRegion.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
          const byRegion = all.filter((c) => {
            const cn = (c.city || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            const rn = (c.region || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            return cn.includes(kw) || rn === kw;
          });
          if (byRegion.length >= 3) list = byRegion;
        }
        setItems(list);
      })
      .catch((e) => { if (!cancelled && e?.name !== "CanceledError") setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; controller.abort(); };
  }, [authLoading, near, user, activeCountry, activeRegion, activeCity]);

  const localCount = items.length;
  const locationLabel = useMemo(() => {
    if (!near) return null;
    if (activeCity) return `${activeCity}, ${activeCountry}`;
    if (activeRegion) return `${activeRegion}, ${activeCountry}`;
    return activeCountry;
  }, [near, activeCity, activeRegion, activeCountry]);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-[#1E3A8A]" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-[#1E3A8A]">{t(lang, "internships_title")}</h1>
            <p className="text-slate-600 text-sm">{t(lang, "internships_sub")}</p>
          </div>
        </div>

        {/* Location filter chips */}
        <div className="mt-4 mb-6 flex items-center gap-2 flex-wrap">
          <Button
            data-testid="intern-filter-all"
            variant={!near ? "default" : "outline"}
            size="sm"
            onClick={() => { params.delete("near"); setParams(params); }}
            className={`rounded-full ${!near ? "bg-[#1E3A8A] hover:bg-[#172554]" : ""}`}
          >
            {t(lang, "internships_all")}
          </Button>
          <Button
            data-testid="intern-filter-near-me"
            variant={near ? "default" : "outline"}
            size="sm"
            onClick={() => { params.set("near", "me"); setParams(params); }}
            disabled={!canFilterNear}
            className={`rounded-full ${near ? "bg-[#F97316] hover:bg-[#EA580C]" : ""}`}
          >
            <MapPin className="w-3.5 h-3.5 mr-1" /> {t(lang, "internships_near_me")}
          </Button>
          {!canFilterNear && (
            <Link to="/profile" data-testid="intern-setup-location" className="text-xs text-[#1E3A8A] underline underline-offset-4 hover:text-[#F97316]">
              <Compass className="w-3 h-3 inline mr-1" /> {t(lang, "internships_setup_location")}
            </Link>
          )}
          {near && locationLabel && (
            <Badge variant="secondary" className="rounded-full">{locationLabel}</Badge>
          )}
        </div>

        <p className="text-sm text-slate-500 mb-6" data-testid="intern-count">
          {loading ? "…" : `${localCount} ${t(lang, "companies_found")}`}
        </p>

        {!loading && localCount === 0 && (
          <div className="rounded-3xl bg-slate-50 border border-dashed border-slate-200 p-10 text-center">
            <MapPin className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">{t(lang, "internships_empty")}</p>
            {near && (
              <Button
                data-testid="intern-empty-clear"
                variant="link"
                onClick={() => { params.delete("near"); setParams(params); }}
                className="mt-2 text-[#F97316]"
              >
                {t(lang, "internships_show_all")}
              </Button>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <div key={c.id} data-testid={`intern-card-${c.id}`} className="rounded-2xl bg-white border border-slate-200 p-5 hover:-translate-y-1 hover:shadow-md transition-transform fade-up">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-display font-semibold text-lg">{c.name}</p>
                  <p className="text-xs text-slate-500">{c.city}, {c.country}</p>
                </div>
                <Badge className="bg-[#1E3A8A] text-white rounded-full text-[10px]">Stage</Badge>
              </div>
              <p className="text-sm text-slate-600 mt-3 line-clamp-3">{c.description}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {c.typical_needs?.slice(0, 2).map((n) => <Badge key={n} variant="secondary" className="rounded-full text-[10px]">{n}</Badge>)}
              </div>
              <Button data-testid={`intern-view-${c.id}`} size="sm" onClick={() => setSelected(c)} className="mt-4 w-full rounded-full bg-[#1E3A8A] hover:bg-[#172554]">
                {lang === "fr" ? "Voir la fiche" : "View profile"}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {selected && <CompanySheet company={selected} onClose={() => setSelected(null)} onApply={() => setApply(true)} />}
      <ApplyDialog open={apply} onOpenChange={setApply} company={selected} />
    </Layout>
  );
}
